import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, throwError } from 'rxjs';
import { SsoDomain } from '../models/SsoDomain';
import { map, catchError } from 'rxjs/operators';
import { SsoDomainResponse, SsoDomainsListResponse } from '../models/ApiResponses';
import { convertKeysToCamelCase } from '../../shared/utilities/json-key-transform';

@Injectable({
  providedIn: 'root'
})
export class SsoDomainService {
  private http = inject(HttpClient);
  private apiUrl = environment.ssoDomainManagementApi.serviceBaseUrl;
  blockedDomains = [ // Blocked domains must be in lowercase
    'gmail.com',
    'googlemail.com',
    'yahoo.com',
    'outlook.com',
    'hotmail.com',
    'live.com',
    'msn.com',
    'icloud.com'
  ];

  getSsoDomains(): Observable<SsoDomain[]> {
    return this.http.get<SsoDomainsListResponse>(this.apiUrl)
      .pipe(
        map(response => {
          let camelCase = convertKeysToCamelCase(response) as SsoDomainsListResponse;
          return camelCase.ssoDomains;
        })
      );
  }

  createSsoDomain(domainValue: string): Observable<SsoDomain> {
    if (!this.validateDomain(domainValue)) {
      return throwError(() => 'Invalid domain format.');
    }

    const ssoDomain = { ssoDomainValue: domainValue };
    return this.http.post<SsoDomainResponse>(this.apiUrl, ssoDomain).pipe(
      map(response => {
        let camelCase = convertKeysToCamelCase(response) as SsoDomainResponse;
        if (camelCase.error !== null) {
          throw new Error(camelCase.error || 'An unexpected error occurred');
        }
        return camelCase.ssoDomain;
      }),
      catchError(error => {
        error.error = convertKeysToCamelCase(error.error) as SsoDomainResponse;
        return this.handleError(error);
      })
    );
  }

  updateSsoDomain(domain: SsoDomain, newSsoDomainValue: string): Observable<SsoDomain> {
    if (!this.validateDomain(newSsoDomainValue)) {
      return throwError(() => 'Invalid domain format.');
    }

    if (domain.numberOfUsers > 0) {
      return throwError(() => 'Cannot update domain with active users.');
    }

    const updatedDomain = {
      newSsoDomainValue: newSsoDomainValue,
      ssoDomainId: domain.ssoDomainId,
    };

    return this.http.put<SsoDomainResponse>(this.apiUrl, updatedDomain).pipe(
      map(response => {
        let camelCase = convertKeysToCamelCase(response) as SsoDomainResponse;
        if (camelCase.error !== null) {
          throw new Error(camelCase.error || 'An unexpected error occurred');
        }
        return camelCase.ssoDomain;
      }),
      catchError(error => {
        error.error = convertKeysToCamelCase(error.error) as SsoDomainResponse;
        return this.handleError(error);
      })
        
    );
  }

  private validateDomain(domain: string): boolean {
    const domainRegex = /^(?!-)(xn--)?([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,63}$/;

    if (!domainRegex.test(domain)) {
      return false;
    }

    return !this.blockedDomains.includes(domain.toLowerCase());
  }

  private handleError(error: any): Observable<never> {
    if (error?.error?.error) {
      const apiError = error.error as SsoDomainResponse;
      let errorMessage: string;

      switch (apiError.error) {
        case 'SsoDomainAlreadyExists':
        case 'SsoDomainValueAlreadyExists':
          errorMessage = 'That domain already exists.';
          break;
        default:
          errorMessage = 'An unexpected error occurred.';
      }

      return throwError(() => errorMessage);
    }
    return throwError(() => 'An unexpected error occurred.');
  }

}
